/**
 * The staff Book pre-flight — the member page's and the calendar's Book
 * modals (2026-10-30, `credit-liveness-group-b`).
 *
 * A `book_session` for a member with no usable credit fails with
 * `insufficient_credits`, but the saga can take minutes to reach that terminal
 * state, so both modals read the member's credit first and jump straight to
 * the remedy (buy credits) when the member DEFINITIVELY has none. "Usable" is
 * each list row's `is_usable`, the booking gate's own rule as
 * `list_member_packages` / `list_member_memberships` compute it, never
 * re-derived here. Until this change each modal carried its own copy: any
 * package with sessions left (an expired one included) and ANY active
 * membership (one used up for this cycle included) counted as credit, so the
 * modal dispatched a booking the saga then refused.
 *
 * Lives apart from the two components so the rule can be proven: the SPA tree
 * is ESLint-ignored and coverage-excluded, so a unit test is its only guard.
 * The dispatcher is a parameter (the pages pass `dispatchAction`) so that test
 * drives the whole check without a Supabase session.
 */

import type { DispatchResult } from "./dispatch";

/** The page size the pre-flight asks for: the list actions' maximum. */
export const BOOK_PREFLIGHT_LIST_LIMIT = 200;

/** The part of a credit-list row the pre-flight reads. */
interface UsableFlagRow {
  readonly is_usable?: boolean;
}

/** Both list actions' response, as far as the pre-flight reads it. */
export interface CreditListResponse {
  readonly member_packages?: ReadonlyArray<UsableFlagRow>;
  readonly member_memberships?: ReadonlyArray<UsableFlagRow>;
  readonly next_cursor?: string | null;
}

export type BookPreflightDispatch = (
  action: "list_member_packages" | "list_member_memberships",
  input: Record<string, unknown>,
) => Promise<DispatchResult<CreditListResponse>>;

/**
 * True only when the member definitively holds no credit a booking could draw
 * on: both reads succeeded, both are complete, every row carries the flag and
 * none is usable. Anything less decides nothing (false), and `book_session`
 * stays the authority.
 */
export async function bookPreflightFindsNoCredit(
  memberId: string,
  dispatch: BookPreflightDispatch,
): Promise<boolean> {
  const input = () => ({
    member_id: memberId,
    status: "active",
    limit: BOOK_PREFLIGHT_LIST_LIMIT,
  });
  const [pkgRes, memRes] = await Promise.all([
    dispatch("list_member_packages", input()),
    dispatch("list_member_memberships", input()),
  ]);
  if (!pkgRes.ok || !memRes.ok) return false;
  const rows = [
    ...(pkgRes.response.member_packages ?? []),
    ...(memRes.response.member_memberships ?? []),
  ];
  // A row without the flag comes from a server that does not compute it yet:
  // its answer is unknown, never "no credit".
  if (rows.some((r) => typeof r.is_usable !== "boolean")) return false;
  if (rows.some((r) => r.is_usable === true)) return false;
  // Only a COMPLETE read is definitive: past a full page, a usable credit may
  // sit on the next one.
  return (
    (pkgRes.response.next_cursor ?? null) === null &&
    (memRes.response.next_cursor ?? null) === null
  );
}
